import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, EyeOff, Send, Sparkles } from "lucide-react";
import useScreenGuard from "./useScreenGuard";

// QP AI chat - what the fullscreen world map turns into once unlocked.
// For now it talks to the existing QP Assistant endpoint (Gemini, /api/chat/message)
// with its own throwaway session; a dedicated QP AI directive is a later backend step.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const GREETING = {
  role: "assistant",
  text: "Welcome to QP AI. I'm connected to QP Assistant for now - ask me anything about Quality Power."
};

const QpAiChat = ({ onClose }) => {
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionIdRef = useRef(`qpai_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const { shielded, resume, guardProps } = useScreenGuard();
  const [openedAt] = useState(() => new Date().toLocaleString("en-GB"));

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const setLast = (fn) =>
    setMessages((prev) => {
      const next = [...prev];
      next[next.length - 1] = fn(next[next.length - 1]);
      return next;
    });

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionIdRef.current, message: text })
      });
      const type = res.headers.get("content-type") || "";
      if (!res.ok || !res.body || !type.includes("text/event-stream")) {
        throw new Error(`HTTP ${res.status} ${res.statusText}, content-type "${type}"`.replace(" ,", ","));
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let received = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop();
        for (const ev of events) {
          if (!ev.startsWith("data: ")) continue;
          const payload = JSON.parse(ev.slice(6));
          if (payload.delta) {
            received += payload.delta;
            setLast((m) => ({ ...m, text: m.text + payload.delta }));
          }
        }
      }
      if (!received) throw new Error("the stream closed without any reply text");
    } catch (e) {
      // Standing policy: surface the real error so it can be reported back.
      setLast(() => ({ role: "assistant", text: `QP AI couldn't reach the assistant (${e.name}: ${e.message}).`, error: true }));
    } finally {
      setStreaming(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className="qpai-panel qpai-guard relative w-full h-full flex flex-col rounded-2xl overflow-hidden" {...guardProps}>
      {/* Watermark - marks any capture that gets past the guards. */}
      <div className="qpai-watermark" aria-hidden="true" data-mark={`QP AI · CONFIDENTIAL · ${openedAt}`} />
      {shielded && (
        <button
          type="button"
          onClick={() => {
            resume();
            inputRef.current?.focus();
          }}
          className="qpai-shield absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 text-sm"
        >
          <EyeOff size={22} />
          QP AI is hidden while this window isn't in focus.
          <span className="qpai-muted text-xs">Click to continue</span>
        </button>
      )}
      <div className="qpai-bar flex items-center justify-between gap-3 px-4 sm:px-6 py-3">
        <div className="flex items-center gap-2.5">
          <span className="qpai-orb" aria-hidden="true" />
          <div>
            <p className="qpai-title text-sm sm:text-base font-bold tracking-wide">QP AI</p>
            <p className="qpai-muted text-[11px]">Connected to QP Assistant</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="qpai-chip inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium focus:outline-none focus-visible:ring-2"
        >
          <ArrowLeft size={14} /> Back to map
        </button>
      </div>
      <div className="qpai-rule h-[3px]" />

      <div ref={scrollRef} className={`qpai-noselect flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-5 space-y-3 ${shielded ? "qpai-blurred" : ""}`} aria-live="polite">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] sm:max-w-[70%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user" ? "qpai-user" : m.error ? "qpai-error" : "qpai-bot"
              }`}
            >
              {m.role === "assistant" && i === 0 && <Sparkles size={13} className="inline mr-1.5 -mt-0.5" />}
              {m.text || (streaming && i === messages.length - 1 ? <span className="qpai-typing">•••</span> : "")}
            </div>
          </div>
        ))}
      </div>

      <form
        className="qpai-bar flex items-center gap-2 px-3 sm:px-5 py-3"
        onSubmit={(e) => {
          e.preventDefault();
          send();
        }}
      >
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask QP AI…"
          aria-label="Message QP AI"
          className="qpai-input flex-1 min-w-0 rounded-full px-4 py-2.5 text-sm focus:outline-none"
        />
        <button
          type="submit"
          disabled={streaming || !input.trim()}
          aria-label="Send"
          className="qpai-send w-10 h-10 shrink-0 rounded-full flex items-center justify-center disabled:opacity-40"
        >
          <Send size={16} />
        </button>
      </form>
    </div>
  );
};

export default QpAiChat;
